export type ParsedRow = {
  lineNumber: number;
  cells: string[];
  subscriptionId: string;
  valid: boolean;
};

export type ParseResult = {
  headers: string[];
  subscriptionIdIndex: number;
  rows: ParsedRow[];
  validCount: number;
  invalidCount: number;
  duplicateCount: number;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const MAX_ROWS = 50_000;

function detectDelimiter(header: string): "," | "\t" {
  const tabs = header.split("\t").length - 1;
  const commas = header.split(",").length - 1;
  return tabs > commas ? "\t" : ",";
}

type HeaderMatch = {
  headerIndex: number;
  delimiter: "," | "\t";
  idIndex: number;
};

function findHeaderRow(lines: Array<{ line: string; lineNumber: number }>): HeaderMatch | null {
  for (let i = 0; i < lines.length; i++) {
    const delimiter = detectDelimiter(lines[i].line);
    const columns = lines[i].line.split(delimiter).map(col => col.trim().toLowerCase());
    const idIndex = columns.indexOf("subscription_id");
    if (idIndex !== -1) {
      return { headerIndex: i, delimiter, idIndex };
    }
  }
  return null;
}

function splitCsvLine(line: string, delimiter: "," | "\t"): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === delimiter) {
        result.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
  }
  result.push(current.trim());
  return result;
}

export function parseCsv(text: string): ParseResult {
  const lines = text
    .replace(/^\u{FEFF}/u, "")
    .split(/\r?\n/)
    .map((line, index) => ({ line, lineNumber: index + 1 }))
    .filter(entry => entry.line.trim() !== "");

  if (lines.length === 0) {
    throw new Error("The file is empty.");
  }

  const header = findHeaderRow(lines);
  if (!header) {
    throw new Error('No header row found containing "subscription_id".');
  }

  const headers = splitCsvLine(lines[header.headerIndex].line, header.delimiter);

  const dataLines = lines.slice(header.headerIndex + 1);

  if (dataLines.length > MAX_ROWS) {
    throw new Error(
      `That file has ${dataLines.length.toLocaleString()} data rows, above the ${MAX_ROWS.toLocaleString()} row limit.`,
    );
  }

  const allRows: ParsedRow[] = dataLines.map(({ line, lineNumber }) => {
    const cells = splitCsvLine(line, header.delimiter);
    const subscriptionId = cells[header.idIndex] ?? "";

    return {
      lineNumber,
      cells,
      subscriptionId,
      valid: UUID_PATTERN.test(subscriptionId),
    };
  });

  const seen = new Set<string>();
  const rows: ParsedRow[] = [];
  let duplicateCount = 0;

  for (const row of allRows) {
    if (row.valid && seen.has(row.subscriptionId)) {
      duplicateCount++;
      continue;
    }
    if (row.valid) seen.add(row.subscriptionId);
    rows.push(row);
  }

  return {
    headers,
    subscriptionIdIndex: header.idIndex,
    rows,
    validCount: rows.filter(r => r.valid).length,
    invalidCount: rows.filter(r => !r.valid).length,
    duplicateCount,
  };
}
